// Customer validators

import { z } from 'zod';
import {
  phoneSchema,
  addressSchema,
  currencyCodeSchema,
  customerTypeEnumSchema,
  customerStatusEnumSchema,
  communicationChannelEnumSchema,
} from '../utils/validation';

// Create customer validator
export const createCustomerSchema = z.object({
  companyName: z
    .string()
    .min(2, 'Company name must be at least 2 characters')
    .max(200, 'Company name cannot exceed 200 characters'),
  displayName: z.string().max(100, 'Display name cannot exceed 100 characters').optional(),
  customerType: customerTypeEnumSchema,
  billingAddress: addressSchema,
  shippingAddress: addressSchema.optional(),
  paymentTerms: z
    .object({
      paymentDueDays: z.number().int().min(1).max(365),
      currency: currencyCodeSchema,
      paymentMethods: z.array(z.string()),
      creditLimit: z.number().positive().optional(),
    })
    .optional(),
  taxExempt: z.boolean().optional(),
  taxId: z.string().optional(),
  primaryContactId: z.string().optional(),
});

// Update customer validator
export const updateCustomerSchema = z.object({
  companyName: z
    .string()
    .min(2, 'Company name must be at least 2 characters')
    .max(200, 'Company name cannot exceed 200 characters')
    .optional(),
  displayName: z.string().max(100, 'Display name cannot exceed 100 characters').optional(),
  customerType: customerTypeEnumSchema.optional(),
  billingAddress: addressSchema.optional(),
  shippingAddress: addressSchema.optional(),
  paymentTerms: z
    .object({
      paymentDueDays: z.number().int().min(1).max(365),
      currency: currencyCodeSchema,
      paymentMethods: z.array(z.string()),
      creditLimit: z.number().positive().optional(),
    })
    .optional(),
  taxExempt: z.boolean().optional(),
  taxId: z.string().optional(),
  primaryContactId: z.string().optional(),
  status: customerStatusEnumSchema.optional(),
});

// Contact validator
export const createContactSchema = z.object({
  customerId: z.string().min(1, 'Customer ID is required'),
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name cannot exceed 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name cannot exceed 50 characters'),
  jobTitle: z.string().max(100, 'Job title cannot exceed 100 characters').optional(),
  department: z.string().max(100, 'Department cannot exceed 100 characters').optional(),
  email: z.string().email('Invalid email address'),
  phone: phoneSchema.optional(),
  whatsappOptIn: z.boolean().optional(),
  preferredChannel: communicationChannelEnumSchema.optional(),
  isPrimaryContact: z.boolean().optional(),
  canReceiveInvoices: z.boolean().optional(),
  canMakePayments: z.boolean().optional(),
  notes: z.string().max(1000, 'Notes cannot exceed 1000 characters').optional(),
  tags: z.array(z.string()).max(10, 'Cannot have more than 10 tags').optional(),
});

export const updateContactSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name cannot exceed 50 characters')
    .optional(),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name cannot exceed 50 characters')
    .optional(),
  jobTitle: z.string().max(100, 'Job title cannot exceed 100 characters').optional(),
  department: z.string().max(100, 'Department cannot exceed 100 characters').optional(),
  email: z.string().email('Invalid email address').optional(),
  phone: phoneSchema.optional(),
  whatsappOptIn: z.boolean().optional(),
  preferredChannel: communicationChannelEnumSchema.optional(),
  isPrimaryContact: z.boolean().optional(),
  canReceiveInvoices: z.boolean().optional(),
  canMakePayments: z.boolean().optional(),
  notes: z.string().max(1000, 'Notes cannot exceed 1000 characters').optional(),
  tags: z.array(z.string()).max(10, 'Cannot have more than 10 tags').optional(),
});

// Customer search/filter validator
export const customerFilterSchema = z.object({
  customerType: customerTypeEnumSchema.optional(),
  status: customerStatusEnumSchema.optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(100).optional(),
  sortBy: z.enum(['companyName', 'createdAt', 'totalInvoiced']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

// Customer search validator
export const customerSearchSchema = z.object({
  q: z
    .string()
    .min(1, 'Search query is required')
    .max(100, 'Search query cannot exceed 100 characters'),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(100).optional(),
});

// Customer credit validation validator
export const customerCreditValidationSchema = z.object({
  amount: z.number().positive('Amount must be positive'),
});

// Types
export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;
export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>;
export type CreateContactInput = z.infer<typeof createContactSchema>;
export type UpdateContactInput = z.infer<typeof updateContactSchema>;
export type CustomerFilterInput = z.infer<typeof customerFilterSchema>;
export type CustomerSearchInput = z.infer<typeof customerSearchSchema>;
export type CustomerCreditValidationInput = z.infer<typeof customerCreditValidationSchema>;
